import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useRebaseClient, useSnackbarController } from "@rebasepro/app";
import type { RebaseClient } from "@rebasepro/types";
import {
    Alert as RebaseAlert,
    Button as RebaseButton,
    Card as RebaseCard,
    Chip as RebaseChip,
    CircularProgressCenter as RebaseCircularProgressCenter,
    Typography as RebaseTypography,
    cls,
    cardClickableMixin,
    iconSize
} from "@rebasepro/ui";
import { PenLine as RebasePenIcon } from "lucide-react";

import { EmailComposer, type ComposerTarget } from "./EmailComposer";
import { useOpenConversation } from "./ConversationPanel";
import {
    callFunction,
    compareThreads,
    daysWaiting,
    sinceLabel,
    threadTitle,
    threadWith,
    type MailboxStatus,
    type ThreadRow
} from "./_email_shared";

const Alert = RebaseAlert as React.ComponentType<any>;
const Button = RebaseButton as React.ComponentType<any>;
const Card = RebaseCard as React.ComponentType<any>;
const Chip = RebaseChip as React.ComponentType<any>;
const CircularProgressCenter = RebaseCircularProgressCenter as React.ComponentType<any>;
const Typography = RebaseTypography as React.ComponentType<any>;
const PenIcon = RebasePenIcon as React.ComponentType<any>;

/**
 * The correspondence with one candidate or one company, on their own screen.
 *
 * "Correo" is the whole mailbox and it is the wrong place to answer "what have
 * we said to *this* person" — that question is asked while looking at them, and
 * an operator who has to leave the record, find the thread in a list of four
 * hundred and come back has been given a worse version of Gmail.
 *
 * So the same threads appear here, filtered to this entity, with a composer that
 * already knows the address and files what it sends against this record. The
 * link it writes is `manual`: a person opened this candidate and pressed
 * Escribir, which is a stronger statement about what the conversation is about
 * than any guess the poller could make, and ingest will never overwrite it.
 *
 * A conversation opens in the side panel every conversation outside Correo
 * opens in (ConversationPanel.tsx). It used to unfold here, under the list —
 * below the fold once there were a few, and with no way to answer, so the one
 * thing you would do after reading it still meant leaving the record.
 *
 * Mounted as an `entityViews` tab on `talents` and `companies` from
 * frontend/src/App.tsx, which is where React components for collections have to
 * be registered — the backend imports the shared config and must not pull in a
 * tree of JSX.
 */

export type EmailEntityKind = "talent" | "company";

const RECENT_LIMIT = 50;

export function EntityEmailPanel({
    kind,
    entityId,
    /** Prefilled recipient. The candidate's own address, the company's contact. */
    email,
    displayName,
    /**
     * The same employer's prospect identity, when this record has one.
     *
     * A company could be three months into an outreach thread and read as never
     * contacted on its own screen, because every prospecting email is filed as
     * `entity_type = 'prospect'` against a name-derived key while this panel
     * asked only for `entity_type = 'company'` against a uuid. Two disjoint
     * histories for one business, neither mentioning the other. 0044 put
     * `employer_key` on `companies`; this is the half that reads it.
     */
    employerKey,
    recipientName,
    showCompose = true
}: {
    kind: EmailEntityKind;
    /** Empty is allowed for a company known only by its prospect key. */
    entityId: string;
    email: string | null;
    displayName: string | null;
    employerKey?: string | null;
    /** The person behind `email`, for the templates' greeting. */
    recipientName?: string | null;
    /** Off where another composer already sits on the same screen. */
    showCompose?: boolean;
}) {
    const client = useRebaseClient<RebaseClient>();
    const snackbar = useSnackbarController();

    const [threads, setThreads] = useState<ThreadRow[] | null>(null);
    const [status, setStatus] = useState<MailboxStatus | null>(null);
    const [composing, setComposing] = useState(false);
    const [error, setError] = useState("");

    const data = (client as any)?.data;

    const load = useCallback(async () => {
        if (!data || (!entityId && !employerKey)) return;
        setError("");
        try {
            // Two queries rather than one: the filter syntax is a conjunction of
            // per-field comparisons and cannot express "this record OR that
            // key". Merged by id below, because a thread can legitimately match
            // both once somebody links it by hand. An employer with no company
            // row — most of the prospect list — has only the second.
            const queries = [];
            if (entityId) {
                queries.push(data.collection("email-threads").find({
                    where: { entity_type: ["==", kind], entity_id: ["==", entityId] },
                    orderBy: ["last_message_at", "desc"],
                    limit: RECENT_LIMIT
                }));
            }
            if (kind === "company" && employerKey) {
                queries.push(data.collection("email-threads").find({
                    where: { entity_type: ["==", "prospect"], employer_key: ["==", employerKey] },
                    orderBy: ["last_message_at", "desc"],
                    limit: RECENT_LIMIT
                }));
            }
            const pages = await Promise.all(queries);
            const seen = new Map<string, ThreadRow>();
            for (const page of pages) {
                for (const row of ((page?.data ?? []) as ThreadRow[])) {
                    if (row?.id) seen.set(row.id, row);
                }
            }
            setThreads([...seen.values()]);
        } catch (err: any) {
            setError(err?.message || "No se pudieron cargar las conversaciones");
            setThreads([]);
        }
    }, [data, kind, entityId, employerKey]);

    useEffect(() => { void load(); }, [load]);

    useEffect(() => {
        callFunction<MailboxStatus>(client, "sync-inbox")
            .then(setStatus)
            // The panel works without the status; only the "can I send" banner
            // and the disabled state depend on it.
            .catch(() => setStatus(null));
    }, [client]);

    // Reloads this list after the panel writes anything — reading clears
    // "sin leer", answering clears "esperan".
    const openConversation = useOpenConversation(load);

    const ordered = useMemo(() => [...(threads ?? [])].sort(compareThreads), [threads]);

    const target = useMemo<ComposerTarget>(() => ({
        to: email ? [email] : [],
        subject: "",
        threadId: null,
        // A person's decision, and it sticks: `linked_by = manual`, which the
        // poller will never overwrite with a guess from an address.
        // Filed against the record when there is one, against the prospect
        // key when there is only that. Both are a person's decision.
        link: entityId
            ? { entity_type: kind, entity_id: entityId }
            : { entity_type: "prospect" as const, employer_key: employerKey },
        draftContext: {
            entity_type: entityId ? kind : "prospect",
            entity_id: entityId || undefined,
            employer_key: employerKey ?? undefined,
            tone: kind === "talent" ? "candidato" : "primer_contacto"
        },
        ...(kind === "talent" ? { recipientName: displayName } : { employerName: displayName, recipientName: recipientName ?? null })
    }), [email, kind, entityId, displayName, employerKey, recipientName]);

    if (!entityId && !employerKey) {
        return (
            <div className="p-4">
                <Typography variant="body1" color="secondary">
                    Guarda la ficha antes de escribir: el correo se queda enganchado a una ficha
                    que ya existe.
                </Typography>
            </div>
        );
    }

    return (
        <div className="p-4 flex flex-col gap-4 max-w-4xl">
            <div className="flex items-center gap-2 flex-wrap">
                <Typography variant="subtitle2" component="h3" className="grow">
                    Correo con {displayName || (kind === "talent" ? "este candidato" : "esta empresa")}
                </Typography>
                {/*
                    Never disabled on "no address".

                    It was `disabled={!email && !composing}`, which on the
                    companies tab is permanently true — `companies` has no
                    address of its own, so App.tsx passes `email={null}`, and a
                    disabled button can never set `composing`, so the guard could
                    never relax. The whole tab was read-only for companies.

                    An empty "Para" is a field the operator fills in, which is
                    exactly what this panel is for when the address lives on a
                    `company_members` row rather than on the company. The Alert
                    below says so; the composer refuses to *send* without a
                    recipient on its own.
                */}
                {showCompose && (
                    <Button
                        size="small"
                        variant={composing ? "outlined" : "filled"}
                        onClick={() => setComposing(v => !v)}
                    >
                        <PenIcon size={iconSize.smallest} />
                        {composing ? "Cancelar" : "Escribir"}
                    </Button>
                )}
            </div>

            {!email && showCompose && (
                <Alert color="info" size="small">
                    {kind === "company"
                        // Not a defect to fix in the form: `companies` genuinely
                        // has no address column — the people are in
                        // `company_members` — so saying "add it to the record"
                        // would send somebody looking for a field that is not there.
                        ? "Una empresa no tiene un correo propio: quien lo lee es una persona de la empresa. Escribe su dirección a mano en «Para»."
                        : "Esta ficha no tiene email. Añádelo en el formulario, o escribe el destinatario a mano en «Para»."}
                </Alert>
            )}

            {error && <Alert color="error" size="small">{error}</Alert>}

            {composing && (
                <EmailComposer
                    client={client}
                    target={target}
                    canSend={status?.sending !== false}
                    compact
                    onSent={async () => {
                        setComposing(false);
                        await load();
                        snackbar.open({ type: "success", message: "Enviado" });
                    }}
                    onCancel={() => setComposing(false)}
                />
            )}

            {threads === null && <CircularProgressCenter />}

            {threads !== null && ordered.length === 0 && !composing && (
                <Card className="p-4">
                    <Typography variant="body1" color="secondary">
                        Todavía no hay correo con {displayName || "esta ficha"}. Lo que se envíe desde aquí
                        —y lo que respondan— aparecerá en esta pestaña.
                    </Typography>
                </Card>
            )}

            {ordered.length > 0 && (
                <div className="flex flex-col gap-1.5">
                    {ordered.map(thread => {
                        const waiting = daysWaiting(thread);
                        return (
                            <Card
                                key={thread.id}
                                onClick={() => openConversation(thread.id)}
                                className={cls("p-3 flex flex-col gap-1", cardClickableMixin)}
                            >
                                <div className="flex items-center gap-2">
                                    <Typography
                                        variant="body2"
                                        className={cls("truncate grow", thread.unread && "font-bold")}
                                    >
                                        {threadTitle(thread)}
                                    </Typography>
                                    <Typography variant="body2" color="secondary" className="shrink-0">
                                        {sinceLabel(thread.last_message_at)}
                                    </Typography>
                                </div>
                                <div className="flex items-center gap-1.5 flex-wrap">
                                    <Typography variant="body2" color="secondary" className="truncate">
                                        {threadWith(thread)} · {thread.message_count ?? 0} mensaje(s)
                                    </Typography>
                                    {thread.unread && <Chip size="small" colorScheme="greenLighter">sin leer</Chip>}
                                    {waiting !== null && (
                                        <Chip size="small" colorScheme={waiting >= 7 ? "redLighter" : "orangeLighter"}>
                                            {waiting === 0 ? "esperan hoy" : `esperan ${waiting} día${waiting === 1 ? "" : "s"}`}
                                        </Chip>
                                    )}
                                </div>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
